import { useState } from 'react';
import { useMatch } from '@tanstack/react-location';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { SettingsSection } from '@frontend/settings-ui';
import { useDebouncedValue } from '@frontend/design-system';
import { useGetOperatoryV3Data } from '../../../hooks/scheduler-v3/use-get-operatory-v3-data';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { PageConfig } from '../../../types';
import { ManageOperatoryTable } from './manage-operatory-table';
import { ManageOperatoriesV3Table } from './manage-operatory-v3-table';

const defaultPageConfig: PageConfig = {
  page: 1,
  limit: 50,
  searchValue: '',
};

type Props = {
  locationId?: string;
};

export type TableFilters = {
  location: string[];
  source: string[];
  unassigned: boolean;
};

export const ManageOperatoryV3Page = ({ locationId }: Props) => {
  const { params } = useMatch();
  const { navigate } = useSettingsNavigate();
  const { selectedLocationIds } = useSchedulingLocationInfo();
  const selectedLocationId = locationId || selectedLocationIds[0] || '';

  // Temporary change to show unified table for all users until we determine it's safe to keep and can remove the feature flag and the old table.
  const showUnifiedTable = true;

  const [filters, setFilters] = useState<TableFilters>({
    location: selectedLocationIds,
    source: [],
    unassigned: false,
  });

  const [pageConfig, setPageConfig] = useState<PageConfig>(defaultPageConfig);
  const debouncedSearchValue = useDebouncedValue(pageConfig.searchValue);

  // If only unassigned is selected without any locations ids then we want to use all selected location ids to fetch data but apply unassigned filter on the results.
  const effectiveLocationIds =
    filters.unassigned && filters.location.length === 0
      ? selectedLocationIds
      : filters.location.length > 0
      ? filters.location
      : selectedLocationIds;

  const effectiveLocationId = effectiveLocationIds[0] || selectedLocationId;

  const {
    data: operatoriesData,
    isFetching: isFetchingOperatoriesData,
    refetch: refetchOperatoryData,
  } = useGetOperatoryV3Data({
    selectedLocationId: showUnifiedTable ? effectiveLocationId : selectedLocationId,
    debouncedSearchValue,
    pageConfig,
    selectedLocationIds: showUnifiedTable ? effectiveLocationIds : [selectedLocationId],
    sourceIds: filters.source,
  });

  const { data: dataSourcesData, isLoading: isLoadingDataSources } = SchedulerV3.Helpers.useGetDataSourcesForLocation({
    locationId: selectedLocationId,
    isEnabled: !!selectedLocationId,
  });

  // Apply unassigned filter only if it's selected, otherwise show all operatories
  const filteredOperatories = operatoriesData?.operatories
    ? filters.unassigned && filters.location.length === 0
      ? operatoriesData.operatories.filter((operatory) => operatory?.hidden)
      : operatoriesData.operatories
    : [];

  const navigateTo = (operatoryId = '') => {
    if (params.locationId) {
      navigate({
        to: '/schedule/operatories/:locationId/edit/:operatoryId',
        params: { operatoryId, locationId: params.locationId },
      });
    } else {
      navigate({
        to: '/schedule/operatories/edit/:operatoryId',
        params: { operatoryId },
      });
    }
  };

  return (
    <SettingsSection>
      {showUnifiedTable ? (
        <ManageOperatoryTable
          isLoading={isFetchingOperatoriesData || isLoadingDataSources}
          operatoriesData={filteredOperatories || []}
          dataSourcesData={dataSourcesData?.dataSources || []}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          totalCount={operatoriesData?.totalCount || 0}
          refetchOperatoryData={refetchOperatoryData}
          navigateTo={navigateTo}
          setFilters={setFilters}
        />
      ) : (
        <ManageOperatoriesV3Table
          locationId={selectedLocationId}
          isLoading={isFetchingOperatoriesData || isLoadingDataSources}
          operatoriesData={operatoriesData?.operatories || []}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          totalCount={operatoriesData?.totalCount || 0}
          refetchOperatoryData={refetchOperatoryData}
          navigateTo={navigateTo}
        />
      )}
    </SettingsSection>
  );
};
